import { useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Mail,
  Linkedin,
  Github,
  Award,
  Users,
  Code,
  Rocket,
  Target,
  TrendingUp,
  Shield,
  Sparkles,
  ChevronDown,
  CheckCircle2,
  X,
  Lock,
  Bell,
} from 'lucide-react';
import { divisions } from './data/divisions';
import LifeCraft from './pages/LifeCraft';
import LifeCraftPrivacyPolicy from './pages/LifeCraftPrivacyPolicy';
import LifeCraftTermsOfService from './pages/LifeCraftTermsOfService';
import LifeCraftSuccess from './pages/LifeCraftSuccess';
import LifeCraftCancel from './pages/LifeCraftCancel';

// ═══════════════════════════════════════════════════════════════════════════
// 🔒 DEVELOPMENT MODE TOGGLE
// Set to false when ready to launch (allows platform access)
// Set to true to show "Private Beta" modal (blocks platform access)
// ═══════════════════════════════════════════════════════════════════════════
const DEVELOPMENT_MODE = true;

function HomePage() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const { scrollYProgress } = useScroll();
  const navigate = useNavigate();

  // Parallax effects
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Handle platform click - if division has an internal product page, navigate
  // there; otherwise show modal in dev mode or open external URL in production.
  const handlePlatformClick = (url: string, name: string, productPageUrl?: string) => {
    if (productPageUrl) {
      navigate(productPageUrl);
      return;
    }
    if (DEVELOPMENT_MODE) {
      setSelectedPlatform(name);
      setShowComingSoon(true);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const founderCredentials = [
    { icon: Award, text: 'OCI 2025 Multicloud Architect Professional' },
    { icon: Award, text: 'OCI 2025 Generative AI Professional' },
    { icon: Award, text: 'AWS Certified Solutions Architect' },
    { icon: Award, text: 'Azure Fundamentals + AI Fundamentals' },
    { icon: Code, text: '500,000+ Lines of Production Code' },
    { icon: Rocket, text: '19 healthcare platforms built solo in 87 days' },
  ];

  const companyStats = [
    { icon: Code, number: '500K+', label: 'Lines of Code', color: 'from-blue-400 to-purple-400' },
    { icon: Target, number: '25+', label: 'Products', color: 'from-purple-400 to-pink-400' },
    { icon: Users, number: '2M+', label: 'Target Users', color: 'from-pink-400 to-rose-400' },
    { icon: TrendingUp, number: '12', label: 'Divisions', color: 'from-rose-400 to-orange-400' },
  ];

  return (
    <div className="bg-black text-white overflow-x-hidden">
      {/* ═══════════════════════════════════════════════════════════════════════════
          🔒 COMING SOON MODAL - Shows when platforms are clicked in dev mode
          ═══════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {showComingSoon && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            onClick={() => setShowComingSoon(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            
            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 rounded-3xl p-8 max-w-md w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setShowComingSoon(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Lock Icon */}
              <div className="flex justify-center mb-6">
                <motion.div
                  className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center"
                  animate={{
                    boxShadow: [
                      '0 0 20px rgba(124,58,237,0.5)',
                      '0 0 40px rgba(236,72,153,0.5)',
                      '0 0 20px rgba(124,58,237,0.5)',
                    ],
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Lock className="w-10 h-10" />
                </motion.div>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-center mb-2">
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Private Beta
                </span>
              </h3>

              {/* Platform Name */}
              {selectedPlatform && (
                <p className="text-center text-purple-400 font-medium mb-4">
                  {selectedPlatform}
                </p>
              )}

              {/* Description */}
              <p className="text-gray-400 text-center mb-6 leading-relaxed">
                This platform is live and operational. Access is currently restricted to authorized pilot partners, enterprise clients, and scheduled demonstrations.
              </p>

              {/* Launch Timeline */}
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center space-x-2 text-purple-300">
                  <Rocket className="w-5 h-5" />
                  <span className="font-medium">Pilot Program Active</span>
                </div>
              </div>

              {/* Email Signup Placeholder */}
              <div className="space-y-3">
                <p className="text-sm text-gray-500 text-center">
                  Request demo access or partnership inquiry
                </p>
                <div className="flex space-x-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-purple-500 focus:outline-none text-white placeholder-gray-500"
                  />
                  <motion.button
                    className="px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-medium"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Bell className="w-5 h-5" />
                  </motion.button>
                </div>
              </div>

              {/* Back Button */}
              <motion.button
                onClick={() => setShowComingSoon(false)}
                className="w-full mt-6 py-3 border border-white/20 rounded-xl font-medium hover:bg-white/5 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Back to Home
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Beta Disclaimer Banner */}
      <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-purple-600 to-pink-500 text-white py-1 sm:py-2 px-2 sm:px-4 text-center text-xs sm:text-sm font-medium">
        <span>⚠️ <strong>Beta</strong> - These platforms are not a substitute for professional medical advice, diagnosis, or treatment.</span>
      </div>
      {/* Animated background with mouse follow effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(124,58,237,0.4) 0%, rgba(59,130,246,0.2) 50%, transparent 100%)',
            left: mousePosition.x - 300,
            top: mousePosition.y - 300,
          }}
          animate={{
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Floating orbs */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-64 h-64 rounded-full opacity-10 blur-2xl"
            style={{
              background: `radial-gradient(circle, ${i % 2 === 0 ? 'rgba(124,58,237,0.3)' : 'rgba(236,72,153,0.3)'} 0%, transparent 70%)`,
              left: `${20 + i * 15}%`,
              top: `${10 + i * 20}%`,
            }}
            animate={{
              y: [0, -30, 0],
              x: [0, 20, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 5 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.5,
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <motion.nav
        className="fixed top-8 sm:top-10 w-full z-50 backdrop-blur-md bg-black/50 border-b border-white/10"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <motion.div
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
            >
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  OmniAlera
                </h1>
                <p className="text-xs text-gray-400">Technologies Holdings Inc.</p>
              </div>
            </motion.div>

            <div className="hidden md:flex items-center space-x-8">
              <a href="#divisions" className="text-gray-300 hover:text-white transition-colors">Divisions</a>
              <a href="#about" className="text-gray-300 hover:text-white transition-colors">About</a>
              <a href="#contact" className="text-gray-300 hover:text-white transition-colors">Contact</a>
              <motion.button
                className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full font-medium"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Request Demo
              </motion.button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center pt-32 sm:pt-36 md:pt-40">
        <motion.div
          className="max-w-7xl mx-auto px-6 text-center"
          style={{ y: y1, opacity }}
        >
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <motion.div
              className="inline-block mb-6 px-6 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full border border-purple-500/30"
              animate={{
                boxShadow: [
                  '0 0 20px rgba(124,58,237,0.3)',
                  '0 0 40px rgba(236,72,153,0.3)',
                  '0 0 20px rgba(124,58,237,0.3)',
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <span className="text-purple-300 font-medium">🚀 12 Divisions • 25+ Products • Healthcare · Consumer Apps · Gaming</span>
            </motion.div>

            <h1 className="text-6xl md:text-8xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 bg-clip-text text-transparent animate-gradient">
                Transforming African Healthcare
              </span>
            </h1>

            <h2 className="text-3xl md:text-5xl font-bold mb-8 text-gray-300">
              Through Technology
            </h2>

            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-4xl mx-auto leading-relaxed">
              Pan-African technology portfolio built by physician-engineer
              <span className="text-purple-400 font-semibold"> Dr. Omokayode Abiola Aderolu</span> —
              12 divisions across healthcare, consumer apps, and gaming. The foundational
              19 healthcare platforms were built solo in 87 days, with clinical decision
              support, medical education, and AI diagnostics serving healthcare workers
              across 54 countries.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
              <motion.a
                href="#divisions"
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full font-bold text-lg flex items-center space-x-2 shadow-2xl"
                whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(124,58,237,0.5)' }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Explore Platforms</span>
                <ArrowRight className="w-5 h-5" />
              </motion.a>

              <motion.button
                className="px-8 py-4 border-2 border-purple-500/50 rounded-full font-bold text-lg hover:bg-purple-500/10 transition-colors flex items-center space-x-2"
                whileHover={{ scale: 1.05, borderColor: 'rgba(124,58,237,0.8)' }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Request Demo</span>
                <Sparkles className="w-5 h-5" />
              </motion.button>
            </div>

            {/* Animated scroll indicator */}
            <motion.div
              className="flex flex-col items-center space-y-2 text-gray-500"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-sm">Scroll to explore</span>
              <ChevronDown className="w-6 h-6" />
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Company Stats Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 1, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {companyStats.map((stat, index) => (
              <motion.div
                key={index}
                className="relative group"
                initial={{ opacity: 1, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 group-hover:border-white/30 transition-colors">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center mb-4`}>
                    <stat.icon className="w-6 h-6" />
                  </div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Divisions Section */}
      <section id="divisions" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 1, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Our Technology Ecosystem
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              12 Divisions · 25+ Products · One Mission: Transform Healthcare Access in Africa
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {divisions.map((division, index) => (
              <motion.div
                key={division.id}
                initial={{ opacity: 1, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="group relative"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${division.gradient} opacity-0 group-hover:opacity-20 rounded-3xl blur-xl transition-opacity duration-500`} />

                <div className="relative p-8 rounded-3xl bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 group-hover:border-white/30 transition-colors">
                  <div className="flex items-start justify-between mb-6">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${division.gradient} flex items-center justify-center`}>
                      <division.icon className="w-8 h-8" />
                    </div>
                    <span className={`px-4 py-1 rounded-full text-sm font-medium ${
                      division.status === 'Live'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                    }`}>
                      {division.status}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold mb-2">{division.name}</h3>
                  <p className="text-purple-400 font-medium mb-4">{division.tagline}</p>
                  <p className="text-gray-400 mb-6 leading-relaxed">{division.description}</p>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {Object.entries(division.stats).map(([key, value]) => (
                      <div key={key} className="text-center">
                        <div className="text-lg font-bold text-white">{value}</div>
                        <div className="text-xs text-gray-500 capitalize">{key}</div>
                      </div>
                    ))}
                  </div>

                  {/* ═══════════════════════════════════════════════════════════════
                      🔒 MODIFIED: Click shows Private Beta modal in dev mode
                      ═══════════════════════════════════════════════════════════════ */}
                  <motion.button
                    onClick={() => handlePlatformClick(division.url, division.name, division.productPageUrl)}
                    className={`flex items-center justify-center space-x-2 w-full py-3 rounded-xl bg-gradient-to-r ${division.gradient} font-medium`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span>{DEVELOPMENT_MODE ? 'Request Access' : 'Visit Platform'}</span>
                    {DEVELOPMENT_MODE ? <Lock className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section id="about" className="py-20 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 1, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-12 items-center pb-8"
          >
            <div>
              <motion.div
                className="relative w-full aspect-square rounded-3xl overflow-visible"
                whileHover={{ scale: 1.02 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 backdrop-blur-sm flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-64 h-64 mx-auto mb-6 rounded-full overflow-hidden border-4 border-purple-500 shadow-2xl shadow-purple-500/30">
                      <img src="/founder-photo.png" alt="Dr. Omokayode Abiola Aderolu" className="w-full h-full object-cover object-top" />
                    </div>
                    <p className="text-2xl font-bold">Dr. Omokayode Abiola Aderolu</p>
                    <p className="text-purple-400">Founder & CEO</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Meet the Founder
                </span>
              </h2>

              <div className="space-y-4 mb-8">
                <p className="text-lg text-gray-300 leading-relaxed">
                  Physician with <span className="text-purple-400 font-bold">20+ years clinical experience</span> in Nigeria and the UK
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  <span className="text-pink-400 font-bold">MD | MSc Biomedical Engineering</span> • Full-Stack Developer • Cloud Architect
                </p>
                <p className="text-lg text-gray-300 leading-relaxed">
                  Solo-built <span className="text-rose-400 font-bold">500,000+ lines of production code</span> in 87 days
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {founderCredentials.map((cred, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 1, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3 p-3 rounded-lg bg-white/5 border border-white/10"
                  >
                    <cred.icon className="w-5 h-5 text-purple-400 flex-shrink-0" />
                    <span className="text-sm text-gray-300">{cred.text}</span>
                  </motion.div>
                ))}
              </div>

              <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30">
                <h3 className="text-xl font-bold mb-3 flex items-center space-x-2">
                  <Target className="w-5 h-5 text-purple-400" />
                  <span>Mission</span>
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  Transform African healthcare through technology while building generational wealth
                  via non-dilutive funding strategy. Capital-efficient growth strategy,
                  100% founder ownership, sustainable long-term growth.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Funding Strategy Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 1, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Capital-Efficient Growth Strategy
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Building a $1B+ company without equity dilution through strategic grant funding
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 1, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30"
            >
              <TrendingUp className="w-12 h-12 text-purple-400 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Grant Funding</h3>
              <p className="text-gray-400 mb-4">
                Targeting $4M-$7M from NIH SBIR, Gates Foundation, USAID DIV, NSF SBIR
              </p>
              <div className="text-3xl font-bold text-purple-400">$4M-$7M</div>
              <div className="text-sm text-gray-500">Non-dilutive capital</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 1, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30"
            >
              <Shield className="w-12 h-12 text-blue-400 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Infrastructure Credits</h3>
              <p className="text-gray-400 mb-4">
                AWS, Google Cloud, Azure, Oracle Cloud startup programs
              </p>
              <div className="text-3xl font-bold text-blue-400">$750K+</div>
              <div className="text-sm text-gray-500">5-7 year runway</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 1, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/30"
            >
              <CheckCircle2 className="w-12 h-12 text-pink-400 mb-4" />
              <h3 className="text-2xl font-bold mb-3">Founder Ownership</h3>
              <p className="text-gray-400 mb-4">
                100% equity retained • Capital-efficient growth • Complete control
              </p>
              <div className="text-3xl font-bold text-pink-400">100%</div>
              <div className="text-sm text-gray-500">Founder control</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 relative">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 1, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Ready to Transform Healthcare?
              </span>
            </h2>
            <p className="text-xl text-gray-400 mb-12">
              Partner with us to bring world-class healthcare technology to underserved markets
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center mb-12">
              <motion.button
                className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full font-bold text-lg flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Mail className="w-5 h-5" />
                <span>Request Demo</span>
              </motion.button>

              <motion.button
                className="px-8 py-4 border-2 border-purple-500/50 rounded-full font-bold text-lg hover:bg-purple-500/10 transition-colors flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Users className="w-5 h-5" />
                <span>Join Waitlist</span>
              </motion.button>
            </div>

            <div className="flex justify-center space-x-6 mb-12">
              <motion.a
                href="mailto:ceo@omnialera.com"
                className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.1, rotate: 360 }}
                transition={{ duration: 0.3 }}
              >
                <Mail className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.1, rotate: 360 }}
                transition={{ duration: 0.3 }}
              >
                <Linkedin className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="https://github.com/omokayodeaderolu"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.1, rotate: 360 }}
                transition={{ duration: 0.3 }}
              >
                <Github className="w-5 h-5" />
              </motion.a>
            </div>

            <div className="text-gray-500 space-y-2">
              <p>Email: ceo@omnialera.com</p>
              <p>Location: Delaware, USA • Serving African Markets</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-400">© 2025 OmniAlera Technologies Holdings, Inc.</p>
              <p className="text-sm text-gray-500">Delaware C-Corporation</p>
              <p className="text-xs text-gray-600 mt-1">All rights reserved. Proprietary technology — patent pending. Unauthorized reproduction prohibited.</p>
              <a href="/privacy-policy.html" className="text-sm text-purple-300 hover:text-white transition-colors mt-2 inline-block">Privacy Policy</a>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm text-gray-500 max-w-3xl">
              <div>
                <p className="text-purple-300 font-semibold mb-2">LifeCraft</p>
                <Link to="/lifecraft/privacy" className="block hover:text-white transition-colors">Privacy Policy</Link>
                <Link to="/lifecraft/terms" className="block hover:text-white transition-colors">Terms of Service</Link>
              </div>
              <div>
                <p className="text-purple-300 font-semibold mb-2">BellyBloom</p>
                <a href="https://pregnancypro-76a76.web.app/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">Privacy Policy</a>
                <a href="https://pregnancypro-76a76.web.app/terms-of-service.html" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">Terms of Service</a>
              </div>
              <div>
                <p className="text-purple-300 font-semibold mb-2">OmniGems</p>
                <a href="https://omnigems.netlify.app/privacy.html" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">Privacy Policy</a>
                <span className="block text-gray-600 italic">Terms coming soon</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/lifecraft" element={<LifeCraft />} />
      <Route path="/lifecraft/privacy" element={<LifeCraftPrivacyPolicy />} />
      <Route path="/lifecraft/terms" element={<LifeCraftTermsOfService />} />
      <Route path="/lifecraft/success" element={<LifeCraftSuccess />} />
      <Route path="/lifecraft/cancel" element={<LifeCraftCancel />} />
      <Route path="/privacy" element={<Navigate to="/lifecraft/privacy" replace />} />
      <Route path="/terms" element={<Navigate to="/lifecraft/terms" replace />} />
    </Routes>
  );
}

export default App;
